<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {useMainStore} from "@/Stores/store";
import {useRouter} from "vue-router";
import {useStorage} from "@vueuse/core";
import Group from "@/Pages/User/Groups/Group/Group.vue";
import {getInvitations} from "@/Services/invitations";
import {Avatar, Button, Message, Select} from "@/Components/Library";
import {Icon} from "@iconify/vue";
import PageContainer from "@/Components/Global/PageContainer.vue";
import Str from "@/Utils/Str.ts";
import User from "@/Models/User";
import {getGroups} from "@/Services/groups.ts";
import useAnswerProgress from "@/Composables/answerProgress.ts";

const $store = useMainStore();
const router = useRouter();
const $user = new User();
const answerProgress = useAnswerProgress();

const getCurrentUserMember = (group: any) => {
    return group?.members?.find((member: any) => member?.id === $store.user?.id);
};

const groups = computed(() => $store.groups || []);
const storedGroupId = useStorage<string | null>("selected-group-id", groups.value[0]?.id ?? null);
if (!groups.value.length) {
    getGroups({include: ["icons", "membersCount", "group_questionsCount"]}).then(({data}) => {
        console.log(data);
        $store.groups = data;
        storedGroupId.value = data[0]?.id ?? null;
        console.log("Groups loaded", data, storedGroupId.value);
    });
}
const groupId = ref<string | null>(router.currentRoute.value.params.group || storedGroupId.value);
const groupOptions = computed(() => {
    const groups = [...$store.groups];
    if ($user.hasAnyRole(["admin", "super-admin"]) || $user.can("create groups")) {
        groups.unshift({
            id: "CREATE",
            name: "Create new group",
            isCreateOption: true,
        });
    }
    return groups;
});

if (!router.currentRoute.value.params.group || router.currentRoute.value.params.group === "CREATE") {
    storedGroupId.value = groupId.value;
    router.push({name: "feed", params: {group: groupId.value}});
}

const invitations = ref([]);
const showInvitations = useStorage("show.invitations.groups", true);
getInvitations({for: "group", status: "pending"}).then(({data}) => {
    invitations.value = data;
});

const onSelect = async (id: string) => {
    storedGroupId.value = id;
    await router.push({name: "feed", params: {group: id}});
};

watch(groupId, async (val) => {
    if (val === "CREATE") {
        return await router.push({name: "groups.create"});
    }
    onSelect(val);
});
</script>
<template>
    <div>
        <PageContainer class="!pb-4">
            <div v-auto-animate>
                <div v-if="invitations?.length" class="mb-1" v-auto-animate>
                    <div v-if="showInvitations" class="flex flex-col gap-2">
                        <template v-for="invitation in invitations" :key="invitation.id">
                            <Message v-if="invitation.for.id">
                                <template #container>
                                    <div
                                        class="w-full flex-wrap items-center justify-between px-6 py-3 text-sm md:flex"
                                    >
                                        <div class="mb-2 text-center md:mb-0">
                                            <span class="font-bold">
                                                {{ invitation.user?.name }}
                                            </span>
                                            invites you to connect and join
                                            <strong>{{ invitation.for?.name }}</strong>
                                        </div>
                                        <div class="flex items-center justify-center gap-3">
                                            <RouterLink
                                                :to="{
                                                    name: 'groups.invitations.show',
                                                    params: {group: invitation.for.id, invitation: invitation.id},
                                                }"
                                            >
                                                <Button
                                                    class="border border-askr-green !px-3 !py-1 text-sm text-askr-green hover:brightness-110"
                                                >
                                                    View
                                                </Button>
                                            </RouterLink>
                                        </div>
                                    </div>
                                </template>
                            </Message>
                        </template>
                    </div>
                    <div v-else>
                        <Message @click="showInvitations = !showInvitations" class="cursor-pointer">
                            <template #container>
                                <div class="flex w-full items-center gap-2 px-4 py-3 text-sm">
                                    <span
                                        class="flex h-8 w-8 items-center justify-center rounded-full bg-askr-green font-bold text-white"
                                    >
                                        {{ invitations?.length }}
                                    </span>
                                    <h3 class="font-bold">Invitations to groups</h3>
                                </div>
                            </template>
                        </Message>
                    </div>
                </div>
            </div>
            <div class="mt-0 flex w-full justify-end" v-if="invitations?.length">
                <button
                    type="button"
                    class="flex items-center gap-1 text-xs"
                    @click="showInvitations = !showInvitations"
                >
                    <Icon :icon="showInvitations ? 'akar-icons:chevron-up' : 'akar-icons:chevron-down'" />
                    {{ showInvitations ? "Hide" : "Show" }} invitations
                </button>
            </div>

            <!--            <div class="flex items-center gap-3">-->
            <!--                <Select-->
            <!--                    id="group-select"-->
            <!--                    v-model="storedGroupId"-->
            <!--                    :options="groups"-->
            <!--                    optionLabel="name"-->
            <!--                    optionValue="id"-->
            <!--                    placeholder="Select a group"-->
            <!--                    class="min-w-56"-->
            <!--                    @update:modelValue="onSelect"-->
            <!--                />-->
            <!--            </div>-->

            <div v-if="!groups.length" class="rounded-md border border-dashed p-4 text-center dark:border-stone-700">
                You are not a member of any groups yet.
            </div>
        </PageContainer>
        <!--        {{ groupOptions }}-->
        <Group>
            <Select
                id="group-select"
                v-model="groupId"
                :options="groupOptions"
                optionLabel="name"
                optionValue="id"
                placeholder="Select a group"
                class="mr-4 min-w-56 grow"
                filter
                size="large"
                pt:overlay:class="!h-screen"
                pt:listcontainer:class="md:!max-h--screen !grow"
                pt:pcfilter:root:class="w-full"
                :pt:pcfilter:pt-options="{mergeProps: true, mergeSections: true}"
                :pt-options="{mergeProps: true, mergeSections: true}"
            >
                <template #option="{option}">
                    <div v-if="option.isCreateOption" class="flex grow items-center gap-2 text-askr-green">
                        <Icon icon="mdi:plus-circle" width="20" />
                        <span class="font-semibold">{{ option.name }}</span>
                    </div>
                    <div
                        v-else
                        class="flex grow gap-2 border-l-4 pl-2"
                        :class="
                            answerProgress.getTrafficBorderClass(
                                getCurrentUserMember(option)?.answered_count,
                                option?.questions_count,
                            )
                        "
                    >
                        <Avatar
                            class="flex h-10 w-10 shrink-0 text-lg"
                            :class="[
                                `bg-${option.color?.class}`,
                                option.color?.class === 'white' ? 'text-black' : 'text-white',
                            ]"
                            :label="option.icon ? undefined : option.initials"
                            :image="option.icon?.url"
                        />
                        <div class="flex grow flex-col">
                            <div class="flex--wrap flex grow items-start gap-2">
                                <div class="flex flex-col justify-start">
                                    <div class="w-full max-w-full">
                                        <p class="mb-1.5 mt-1 max-w-full text-wrap font-semibold leading-none">
                                            {{ option.name }}
                                        </p>
                                        <div class="mb-3 flex gap-2">
                                            <p v-if="option.user" class="flex items-center gap-1 text-xs">
                                                <Icon icon="mdi:shield-account" width="16" />
                                                {{ option.user?.name }}
                                            </p>
                                            <p class="flex items-center gap-1 text-xs">
                                                <Icon icon="heroicons:eye-solid" width="16" />
                                                <span>{{ option.anonymization.name }}</span>
                                            </p>
                                        </div>
                                        <div class="mb-1.5 flex items-center gap-2">
                                            <div class="flex items-end gap-1 text-xs">
                                                <Icon
                                                    icon="mage:message-question-mark-round-fill"
                                                    width="16"
                                                    :class="
                                                        answerProgress.getTrafficTextClass(
                                                            getCurrentUserMember(option)?.answered_count,
                                                            option?.questions_count,
                                                        )
                                                    "
                                                />
                                                <span>
                                                    ({{
                                                        answerProgress.getAnsweredCount(
                                                            getCurrentUserMember(option)?.answered_count,
                                                            option?.questions_count,
                                                        )
                                                    }}/{{ answerProgress.getTotalCount(option?.questions_count) }})
                                                </span>
                                            </div>
                                            <div
                                                v-if="option.anonymization.slug !== 'invisible' || option.is_owner"
                                                class="ml-2 flex w-8 items-end gap-1"
                                            >
                                                <Icon icon="mdi:account-group" width="16" />
                                                <span class="text-xs">{{ +option.members_count }}</span>
                                            </div>
                                            <div
                                                class="flex items-center gap-1 text-xs"
                                                v-if="true || option.can_admin"
                                            >
                                                <span
                                                    class="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 font-semibold text-white"
                                                >
                                                    {{ +(option?.member_answer_progress_counts?.red ?? 0) }}
                                                </span>
                                                <span
                                                    class="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-600 px-1 font-semibold text-white"
                                                >
                                                    {{ +(option?.member_answer_progress_counts?.yellow ?? 0) }}
                                                </span>
                                                <span
                                                    class="flex h-5 min-w-5 items-center justify-center rounded-full bg-green-600 px-1 font-semibold text-white"
                                                >
                                                    {{ +(option?.member_answer_progress_counts?.green ?? 0) }}
                                                </span>
                                            </div>
                                            <!-- Tried to find icons that would represent anonymization, but IMO it's unintuitive -->
                                            <!--                    <Icon-->
                                            <!--                        :icon="-->
                                            <!--                            {-->
                                            <!--                                full_name: 'mdi:account-details',-->
                                            <!--                                nickname: 'mdi:account-circle',-->
                                            <!--                                initials_only: 'mdi:account-box',-->
                                            <!--                                fully_anonymous: 'mdi:incognito',-->
                                            <!--                                invisible: 'mdi:eye-off',-->
                                            <!--                            }[group.anonymization.slug]-->
                                            <!--                        "-->
                                            <!--                        width="16"-->
                                            <!--                    />-->
                                        </div>
                                    </div>
                                    <p class="mt-1 max-w-full text-wrap text-xs">
                                        {{ Str.of(option.description).truncate().toString() }}
                                    </p>
                                </div>
                                <div class="h--full items--center self--center ml-auto flex pl-2 pt-2 text-stone-500">
                                    <Icon
                                        v-if="option.params?.allow_member_posts"
                                        icon="material-symbols-light:post-add-rounded"
                                        width="18"
                                        class="mr-2"
                                    />
                                    <Icon v-if="option.is_owner" icon="mdi:shield-account" width="18" />
                                    <Icon v-if="option.is_admin" icon="la:user-tie" width="18" />
                                    <Icon v-if="option.is_creator" icon="mingcute:user-edit-line" width="18" />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </Select>
        </Group>
    </div>
</template>
<style scoped></style>
